import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronRight, FlaskConical } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { LabResultSummary } from '../../types/labResults';
import { LabAbnormalSummary, LabStatusBadge } from './LabResultTable';

/**
 * The lab results section of a patient's chart.
 *
 * Renders nothing at all when the patient has no results — not an empty state.
 * Most patients on the platform have never had a lab drawn, and a permanent
 * "No lab results" card on every chart is noise that trains people to skip the
 * region of the page where results will one day appear.
 */
export function PatientLabResults({ patientId }: { patientId: string }) {
  const navigate = useNavigate();
  const [results, setResults] = useState<LabResultSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getLabResults(patientId)
      .then(res => {
        if (cancelled) return;
        if (res.success && res.data) {
          setResults(res.data);
        } else if (res.error) {
          setError(res.error);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // Silent while loading and when there is nothing: the card should appear
  // when it has something to say, not flash an empty shell on every chart.
  if (loading) return null;

  if (error) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      </Card>
    );
  }

  if (!results.length) return null;

  return (
    <Card>
      <div className="divide-y divide-gray-100">
      {results.map(result => (
        <button
          key={result.id}
          onClick={() => navigate(`/lab-results/${result.id}`)}
          className="flex w-full items-center justify-between gap-4 py-4 text-left transition-colors hover:bg-gray-50"
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{result.panelName}</span>
              <LabStatusBadge status={result.status} amended={result.amended} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <LabAbnormalSummary total={result.observationCount} abnormal={result.abnormalCount} />
              <span className="text-xs text-gray-500">
                {formatResultDate(result)}
              </span>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400" />
        </button>
      ))}
      </div>
    </Card>
  );
}

/** Matches the InfoCard shell used elsewhere on the chart. */
function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow" id="lab-results">
      <div className="flex items-center p-4 border-b border-gray-200">
        <FlaskConical className="h-6 w-6 text-gray-500 mr-3" />
        <h3 className="text-lg font-semibold text-gray-900">Lab Results</h3>
      </div>
      <div className="px-6 py-2">{children}</div>
    </div>
  );
}

/**
 * When the result is from.
 *
 * Prefers the lab's own reported date over our received timestamp: the clinical
 * date is when the lab says it reported, and our webhook may have landed later
 * (or, after a redelivery, much later). Falls back to received only when the
 * vendor omitted theirs, which they frequently do.
 */
export function formatResultDate(result: { reportedAt?: string; collectedAt?: string; receivedAt?: string }): string {
  const raw = result.reportedAt || result.collectedAt || result.receivedAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
